import { onNetworkChange } from './network';

/**
 * Всплывашка «Нет соединения с интернетом» — одна на раз (25.09.2026).
 *
 * ЗАЧЕМ. Запрос не дошёл до сервера — ученик должен понять, что дело в сети, а не в нём и не в платформе.
 * До 25.09 он видел «Network Error» строкой на месте ошибки. Текст на месте теперь человеческий
 * (`humanizeError` → `errors.network`), а эта всплывашка — общий знак «действие не удалось из-за связи»
 * на любом экране, включая вход.
 *
 * Полоса «Нет сети» (`OfflineBar`) — СОСТОЯНИЕ: висит, пока связи нет по мнению системы. Всплывашка — СОБЫТИЕ:
 * конкретный запрос не дошёл. Нужны обе: в вагоне с мёртвым Wi-Fi система считает, что связь есть, полосы
 * нет — и о неудаче скажет только всплывашка.
 *
 * ⚠️ НЕ СТОПКОЙ. Без сети падают сразу все запросы экрана, а фоновые опросы — ещё и каждые полминуты.
 * Поэтому: пока сообщение на экране, новые вызовы его не множат и не продлевают; после скрытия новое
 * появится не раньше чем через `COOLDOWN_MS`. Возврат связи паузу сбрасывает — следующий обрыв покажется
 * сразу. В скрытой вкладке не показываем: смотреть на него некому, а пауза съела бы показ, когда
 * человек вернётся.
 *
 * Вызывается из ОДНОГО места — перехватчика ответа `api/client.ts`, когда ответа сервера нет вовсе.
 * Ответ с ошибкой (4xx/5xx) всплывашку не вызывает: у него свой текст на месте.
 */

export const SHOW_MS = 4_000;
export const COOLDOWN_MS = 20_000;

type Listener = (visible: boolean) => void;

let visible = false;
let cooldownUntil = 0;
let hideTimer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<Listener>();

function emit(): void {
  listeners.forEach((l) => {
    try {
      l(visible);
    } catch {
      /* один сломавшийся слушатель не должен мешать остальным */
    }
  });
}

export function reportNetworkError(): void {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  if (visible || Date.now() < cooldownUntil) return;
  visible = true;
  hideTimer = setTimeout(dismissNetworkNotice, SHOW_MS);
  emit();
}

export function dismissNetworkNotice(): void {
  if (hideTimer) clearTimeout(hideTimer);
  hideTimer = undefined;
  if (!visible) return;
  visible = false;
  cooldownUntil = Date.now() + COOLDOWN_MS;
  emit();
}

export function isNetworkNoticeVisible(): boolean {
  return visible;
}

export function onNetworkNotice(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

onNetworkChange((online) => {
  if (online) cooldownUntil = 0;
});

/** Только для проверок: вернуть исходное состояние между сценариями. */
export function resetNetworkNoticeForTests(): void {
  if (hideTimer) clearTimeout(hideTimer);
  hideTimer = undefined;
  visible = false;
  cooldownUntil = 0;
  listeners.clear();
}
